import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { FileCode, FileSpreadsheet, Plus, Pencil, Copy, Trash2, Link2 } from "lucide-react";
import { toast } from "sonner";

const TAAL_LABELS = {
  nl: "Nederlands", en: "Engels", de: "Duits",
  fr: "Frans", es: "Spaans", it: "Italiaans", pt: "Portugees", pl: "Pools", da: "Deens", sv: "Zweeds",
};
const STANDAARD = "_standaard";

function KoppelingKaart({ output, designs, extraTalen, isAdmin, onChanged }) {
  const [nieuweTalen, setNieuweTalen] = useState([]);
  const opties = designs.filter((d) => d.type === output.type);
  const serverTalen = Object.keys(output.talen);
  const talen = [...serverTalen, ...nieuweTalen.filter((t) => !serverTalen.includes(t))];
  const beschikbaar = Object.keys(extraTalen || {}).filter((t) => !talen.includes(t));

  const wijzig = async (taal, waarde) => {
    try {
      await api.put("/template-koppelingen", {
        type: output.type, taal, template_id: waarde === STANDAARD ? null : waarde,
      });
      toast.success("Koppeling opgeslagen");
      if (waarde === STANDAARD) setNieuweTalen((prev) => prev.filter((t) => t !== taal));
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Koppeling opslaan mislukt");
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-5 space-y-3" data-testid={`koppeling-kaart-${output.type}`}>
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Link2 className="h-5 w-5 text-primary" />
        </div>
        <div>
          <div className="font-medium">{output.label}</div>
          <div className="text-xs text-muted-foreground">
            {output.meertalig ? "Template per taal instelbaar" : "Eén template"}
          </div>
        </div>
      </div>
      <div className="space-y-2">
        {talen.map((taal) => {
          const huidige = output.talen[taal]?.template_id || STANDAARD;
          const isExtra = !["nl", "en", "de"].includes(taal);
          return (
            <div key={taal} className="flex items-center gap-2">
              {output.meertalig && (
                <span className="text-xs text-muted-foreground w-24 shrink-0">
                  {TAAL_LABELS[taal] || taal}{isExtra ? " •" : ""}
                </span>
              )}
              <Select value={huidige} onValueChange={(v) => wijzig(taal, v)} disabled={!isAdmin}>
                <SelectTrigger className="h-9 flex-1" data-testid={`koppeling-select-${output.type}-${taal}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={STANDAARD}>{isExtra ? "Standaard (taal verwijderen)" : "Standaard (ingebouwd)"}</SelectItem>
                  {opties.map((d) => <SelectItem key={d.id} value={d.id}>{d.naam}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          );
        })}
        {output.meertalig && isAdmin && beschikbaar.length > 0 && (
          <Select value={undefined} onValueChange={(taal) => setNieuweTalen((prev) => [...prev, taal])}>
            <SelectTrigger className="h-8 w-44 text-xs border-dashed" data-testid={`koppeling-taal-toevoegen-${output.type}`}>
              <SelectValue placeholder="+ Taal toevoegen…" />
            </SelectTrigger>
            <SelectContent>
              {beschikbaar.map((t) => <SelectItem key={t} value={t}>{extraTalen[t] || t}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {nieuweTalen.some((t) => !serverTalen.includes(t)) && (
          <p className="text-[11px] text-muted-foreground">Kies een template bij de nieuwe taal om deze te activeren. Klanten uit dat taalgebied krijgen dan automatisch dit template.</p>
        )}
      </div>
    </div>
  );
}

function NieuwTemplateDialog({ open, onOpenChange, outputs, onCreated }) {
  const [naam, setNaam] = useState("");
  const [type, setType] = useState("pakbon");
  const [taal, setTaal] = useState("nl");
  const [busy, setBusy] = useState(false);
  const meertalig = outputs.find((o) => o.type === type)?.meertalig;

  const maakAan = async () => {
    if (!naam.trim()) { toast.error("Geef het template een naam"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/template-designs", { naam: naam.trim(), type, taal: meertalig ? taal : "nl" });
      toast.success("Template aangemaakt");
      onOpenChange(false);
      setNaam("");
      onCreated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Aanmaken mislukt");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="nieuw-template-dialog">
        <DialogHeader><DialogTitle>Nieuw template</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground">Naam</label>
            <Input value={naam} onChange={(e) => setNaam(e.target.value)} placeholder="Bijv. Pakbon Duitsland"
              data-testid="nieuw-template-naam" autoFocus />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Documenttype</label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="nieuw-template-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {outputs.map((o) => <SelectItem key={o.type} value={o.type}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {meertalig && (
            <div>
              <label className="text-xs text-muted-foreground">Starttaal van de standaardteksten</label>
              <Select value={taal} onValueChange={setTaal}>
                <SelectTrigger data-testid="nieuw-template-taal"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["nl", "en", "de"].map((c) => <SelectItem key={c} value={c}>{TAAL_LABELS[c]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Het template start met de standaard-indeling voor dit documenttype; daarna pas je alles aan in de editor.
          </p>
        </div>
        <DialogFooter>
          <Button onClick={maakAan} disabled={busy} data-testid="nieuw-template-aanmaken">
            {busy ? "Bezig…" : "Aanmaken & bewerken"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Templates() {
  const { user } = useAuth();
  const isAdmin = user?.rol === "admin";
  const navigate = useNavigate();
  const [outputs, setOutputs] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [extraTalen, setExtraTalen] = useState({});
  const [nieuwOpen, setNieuwOpen] = useState(false);
  const [verwijderId, setVerwijderId] = useState(null);

  const load = () => api.get("/templates").then(({ data }) => {
    setOutputs(data.outputs || []);
    setDesigns(data.designs || []);
    setExtraTalen(data.extra_talen || {});
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const typeLabel = (t) => outputs.find((o) => o.type === t)?.label || t;

  const dupliceer = async (id) => {
    try {
      await api.post(`/template-designs/${id}/dupliceer`);
      toast.success("Template gedupliceerd");
      load();
    } catch { toast.error("Dupliceren mislukt"); }
  };

  const verwijder = async () => {
    try {
      await api.delete(`/template-designs/${verwijderId}`);
      toast.success("Template verwijderd");
      load();
    } catch { toast.error("Verwijderen mislukt"); }
    finally { setVerwijderId(null); }
  };

  return (
    <div className="space-y-6" data-testid="templates-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-sm text-muted-foreground">Documenten</div>
          <h1 className="font-display text-4xl font-medium tracking-tight mt-1">Templates</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Stel templates samen met de blok-editor en koppel per documenttype (en taal) welk template
            gebruikt wordt bij het genereren van pakbonnen, facturen, samplebrieven en productsheets.
          </p>
        </div>
        <Button variant="outline" onClick={() => downloadFile("/templates/veld-definities.xlsx", "veld-definities.xlsx").catch(() => toast.error("Download mislukt"))}
          data-testid="veld-definities-download">
          <FileSpreadsheet className="h-4 w-4 mr-1" /> Veld-definities (Excel)
        </Button>
      </div>

      <Tabs defaultValue="koppelingen">
        <TabsList data-testid="templates-tabs">
          <TabsTrigger value="koppelingen" data-testid="tab-koppelingen">Koppelingen</TabsTrigger>
          <TabsTrigger value="bibliotheek" data-testid="tab-bibliotheek">Template-bibliotheek</TabsTrigger>
        </TabsList>

        <TabsContent value="koppelingen" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {outputs.map((o) => (
              <KoppelingKaart key={o.type} output={o} designs={designs} extraTalen={extraTalen} isAdmin={isAdmin} onChanged={load} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="bibliotheek" className="mt-4 space-y-4">
          {isAdmin && (
            <Button onClick={() => setNieuwOpen(true)} data-testid="nieuw-template-knop">
              <Plus className="h-4 w-4 mr-1" /> Nieuw template
            </Button>
          )}
          {designs.length === 0 ? (
            <div className="text-sm text-muted-foreground border border-dashed border-border rounded-xl p-8 text-center">
              Nog geen eigen templates. Maak een nieuw template aan — het start met de standaard-indeling die je vervolgens in de editor aanpast.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {designs.map((d) => (
                <div key={d.id} className="bg-card border border-border rounded-xl p-4 space-y-3" data-testid={`design-kaart-${d.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <FileCode className="h-4.5 w-4.5 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium truncate">{d.naam}</div>
                        <div className="text-xs text-muted-foreground">
                          Bijgewerkt {d.updated_at ? fmtDateTime(d.updated_at) : "—"}
                        </div>
                      </div>
                    </div>
                    <Badge variant="outline" className="shrink-0">{typeLabel(d.type)}</Badge>
                  </div>
                  {isAdmin && (
                    <div className="flex flex-wrap gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => navigate(`/templates/${d.id}/bewerken`)}
                        data-testid={`design-bewerken-${d.id}`}>
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Bewerken
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => dupliceer(d.id)} data-testid={`design-dupliceren-${d.id}`}>
                        <Copy className="h-3.5 w-3.5 mr-1" /> Dupliceren
                      </Button>
                      <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive"
                        onClick={() => setVerwijderId(d.id)} data-testid={`design-verwijderen-${d.id}`}>
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Verwijderen
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <NieuwTemplateDialog open={nieuwOpen} onOpenChange={setNieuwOpen} outputs={outputs}
        onCreated={(d) => navigate(`/templates/${d.id}/bewerken`)} />

      <AlertDialog open={!!verwijderId} onOpenChange={(v) => !v && setVerwijderId(null)}>
        <AlertDialogContent data-testid="verwijder-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Template verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Dit kan niet ongedaan worden gemaakt. Documenttypes die aan dit template gekoppeld zijn, vallen terug op de standaard-indeling.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="verwijder-annuleren">Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={verwijder} data-testid="verwijder-bevestigen">Verwijderen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
